"use client";

import React from "react";
import { ClipboardCheck, Hammer, Ruler, Wrench, CircleCheck, Droplets, Leaf, Shield, ArrowRight, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const processSteps = [{
  id: 1,
  title: "Inspection & Assessment",
  description: "Thorough evaluation of your current siding, underlying structure, and insulation needs.",
  icon: ClipboardCheck
}, {
  id: 2,
  title: "Old Siding Removal",
  description: "Careful dismantling and disposal of existing materials without damaging your home's exterior.",
  icon: Hammer
}, {
  id: 3,
  title: "Surface Preparation",
  description: "Repairing structural issues, installing weather barriers, and ensuring a perfectly flat surface.",
  icon: Ruler
}, {
  id: 4,
  title: "Precision Installation",
  description: "Expert fitting of your chosen siding material according to manufacturer specifications.",
  icon: Wrench
}, {
  id: 5,
  title: "Finishing & Cleanup",
  description: "Applying trim, caulking joints, and conducting a comprehensive site cleanup and final walkthrough.",
  icon: CircleCheck
}];
const materialsData = [{
  id: "vinyl",
  material: "Vinyl Siding",
  cost: "$",
  durability: "Good (20-40 years)",
  appearance: "Versatile, many colors",
  maintenance: "Very Low",
  highlight: false
}, {
  id: "fiber-cement",
  material: "Fiber Cement",
  cost: "$$",
  durability: "Excellent (50+ years)",
  appearance: "Premium, wood-like texture",
  maintenance: "Low",
  highlight: true
}, {
  id: "wood",
  material: "Natural Wood",
  cost: "$$$",
  durability: "Variable (depends on care)",
  appearance: "Authentic, classic beauty",
  maintenance: "High",
  highlight: false
}];
const maintenanceTips = [{
  id: 1,
  title: "Annual Washing",
  description: "Use a soft brush and mild detergent to remove dirt and prevent mold growth.",
  icon: Droplets
}, {
  id: 2,
  title: "Vegetation Control",
  description: "Keep shrubs and trees trimmed at least 2 feet away from the siding to prevent moisture buildup.",
  icon: Leaf
}, {
  id: 3,
  title: "Regular Inspections",
  description: "Check caulking around windows and doors annually to ensure water-tight seals remain intact.",
  icon: Shield
}];
const galleryProjects = [{
  id: 1,
  style: "Craftsman Home",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_1.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_1.png-wvc-sizes"
  }
}, {
  id: 2,
  style: "Modern Colonial",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_2.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_2.png-wvc-sizes"
  }
}, {
  id: 3,
  style: "Ranch Style",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_3.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_3.png-wvc-sizes"
  }
}, {
  id: 4,
  style: "Victorian Restoration",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_4.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_4.png-wvc-sizes"
  }
}, {
  id: 5,
  style: "Contemporary Split-Level",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_5.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_5.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_5.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_5.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_5.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_5.png-wvc-sizes"
  }
}, {
  id: 6,
  style: "Traditional Cape Cod",
  before: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_6.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_before_6.png-wvc-sizes"
  },
  after: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_6.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/siding_after_6.png-wvc-sizes"
  }
}];
export default function SidingInstallationMaterials() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: processRef,
    isVisible: processVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: materialsRef,
    isVisible: materialsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: maintenanceRef,
    isVisible: maintenanceVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: galleryRef,
    isVisible: galleryVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="siding-installation-materials" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-24">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto space-y-4 transition-all duration-700 transform ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-2">Expert Services</Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary">
            Siding Installation & Materials
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Transform your home's exterior with our premium siding solutions. We combine top-tier materials with meticulous installation processes to ensure lasting beauty and protection.
          </p>
        </div>

        {}
        <div ref={processRef} className={`space-y-8 transition-all duration-700 delay-100 transform ${processVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center space-y-2">
            <h3 className="text-2xl md:text-3xl font-semibold font-default">Our 5-Step Installation Process</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">We follow a strict, proven methodology to guarantee flawless results on every project.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative">
            {}
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-border -translate-y-1/2 z-0" />
            
            {processSteps.map((step, i) => <Card key={step.id} data-index={i} className="relative z-10 bg-card border-border shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-primary">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-lg font-default">{step.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center text-sm text-muted-foreground">
                  {step.description}
                </CardContent>
              </Card>)}
          </div>
        </div>

        <Separator className="bg-border/50" />

        {}
      {/*   <div 
         ref={materialsRef}
         className={`space-y-8 transition-all duration-700 transform ${materialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
       >
         <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
           <div className="space-y-2 max-w-2xl">
             <h3 className="text-2xl md:text-3xl font-semibold font-default">Material Comparison</h3>
             <p className="text-muted-foreground">Choose the perfect siding material for your budget, aesthetic preferences, and maintenance capabilities.</p>
           </div>
           <Button variant="outline" asChild>
             <Link to="/ContactPage">Request Material Samples</Link>
           </Button>
         </div>
          <Card className="overflow-hidden border-border shadow-sm">
           <Table>
             <TableHeader className="bg-muted/50">
               <TableRow>
                 <TableHead className="w-[200px] font-default font-semibold">Material</TableHead>
                 <TableHead className="font-default font-semibold">Relative Cost</TableHead>
                 <TableHead className="font-default font-semibold">Durability</TableHead>
                 <TableHead className="font-default font-semibold">Appearance</TableHead>
                 <TableHead className="font-default font-semibold">Maintenance</TableHead>
               </TableRow>
             </TableHeader>
             <TableBody>
               {materialsData.map((row, i) => (
                 <TableRow key={row.id} data-index={i} className={row.highlight ? "bg-primary/5" : ""}>
                   <TableCell className="font-medium font-default">
                     {row.material}
                     {row.highlight && <Badge variant="default" className="ml-2 text-[10px] h-5">Recommended</Badge>}
                   </TableCell>
                   <TableCell className="text-muted-foreground">{row.cost}</TableCell>
                   <TableCell className="text-muted-foreground">{row.durability}</TableCell>
                   <TableCell className="text-muted-foreground">{row.appearance}</TableCell>
                   <TableCell className="text-muted-foreground">{row.maintenance}</TableCell>
                 </TableRow>
               ))}
             </TableBody>
           </Table>
         </Card>
       </div>
       */}
        {}
        <div ref={maintenanceRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 items-center transition-all duration-700 transform ${maintenanceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="lg:col-span-1 space-y-4">
            <Badge variant="outline" className="border-primary text-primary">Care Guide</Badge>
            <h3 className="text-2xl md:text-3xl font-semibold font-default">Keep Your Siding Looking New</h3>
            <p className="text-muted-foreground">
              While we offer low-maintenance options like premium vinyl and fiber cement, a little care goes a long way in extending the life and beauty of your home's exterior.
            </p>
            <div className="pt-4">
              <Button className="w-full sm:w-auto group" asChild>
                <Link to="/FrequentlyAskedQuestionsPage">
                  Read Full Care Guide
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
          
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {maintenanceTips.map((tip, i) => <Card key={tip.id} data-index={i} className="bg-muted/30 border-border shadow-sm">
                <CardHeader className="pb-2">
                  <tip.icon className="w-8 h-8 text-secondary mb-2" />
                  <CardTitle className="text-lg font-default">{tip.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{tip.description}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        <Separator className="bg-border/50" />

        {}
        <div ref={galleryRef} className={`space-y-8 transition-all duration-700 transform ${galleryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center space-y-2">
            <h3 className="text-2xl md:text-3xl font-semibold font-default">Before & After Transformations</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">See the dramatic difference a professional siding replacement can make on various architectural styles.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryProjects.map((project, i) => <Card key={project.id} data-index={i} className="overflow-hidden border-border shadow-md group">
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-lg font-default flex items-center justify-between">
                    {project.style}
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Before</span>
                      <AspectRatio ratio={4 / 3} className="bg-muted rounded-md overflow-hidden">
                        <img src={project.before.src} data-wvc-srcset={project.before.srcset} data-wvc-sizes={project.before.sizes} alt={`${project.style} before siding replacement`} className="object-cover w-full h-full grayscale-[30%] group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                      </AspectRatio>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider">After</span>
                      <AspectRatio ratio={4 / 3} className="bg-muted rounded-md overflow-hidden">
                        <img src={project.after.src} data-wvc-srcset={project.after.srcset} data-wvc-sizes={project.after.sizes} alt={`${project.style} after siding replacement`} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </AspectRatio>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
          
          <div className="text-center pt-8">
            <Button size="lg" className="px-8" asChild>
              <Link to="/ContactPage">Start Your Transformation</Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}